import React, { useMemo } from 'react';
import { useTranslation } from '@lib/useTypedTranslation';
import { StackedColumnCategoryChart, StackedColumnsCategoryChartProps } from '../../../../components/chart/stackedColumnsCategoryChart';
import Card from '@components/card/card';
import '../style.css';
import { createChartTooltipAdapter, getTitle } from './lib';
import { IDevicesAvgDischargeRateIntervalStats } from '../../../../services/shared/device/batteryDevicesService';

interface IProps {
  data: IDevicesAvgDischargeRateIntervalStats,
  isLoading?: boolean
}

interface DeviceAvgDischargeRateChartData {
  bucket: string,
  count: number
}

const series = [
  {
    visible: true,
    dataKey: 'count' as keyof Omit<DeviceAvgDischargeRateChartData, 'date'>,
    description: 'Devices Avg Discharge Rate Count',
    colour: '#1e4263ff'
  },
];

export function DeviceAvgDischargeRateChartContainer(props: IProps) {
  const ns = 'batteryEssentialsChart';
  const { t } = useTranslation(ns);

  const chartData: DeviceAvgDischargeRateChartData[] = useMemo(() => {
    if (!props.data) return [];

    const data = [
      {
        bucket: '0-10',
        count: props.data.ZeroToTen,
      },
      {
        bucket: '10-20',
        count: props.data.TenToTwenty,
      },
      {
        bucket: '20-30',
        count: props.data.TwentyToThirty,
      },
      {
        bucket: '30-40',
        count: props.data.ThirtyToForty,
      },
      {
        bucket: '40-50',
        count: props.data.FortyToFifty,
      },
      {
        bucket: '50-60',
        count: props.data.FiftyToSixty,
      },
      {
        bucket: '60-70',
        count: props.data.SixtyToSeventy,
      },
      {
        bucket: '70-80',
        count: props.data.SeventyToEighty,
      },
      {
        bucket: '80-90',
        count: props.data.EightyToNinety,
      },
      {
        bucket: '90-100',
        count: props.data.NinetyToHundred,
      },
      {
        bucket: '>100',
        count: props.data.HundredAbove,
      }

    ];
    return data;
  }, [props.data]);

  const chartProps: StackedColumnsCategoryChartProps<DeviceAvgDischargeRateChartData> = {
    data: !props.isLoading ? chartData : null,
    series,
    title: getTitle(t, 'DEVICE_AVG_DISCHARGE_CHART_TITLE', true),
    cypressId: 'device-avg-discharge-rate-chart',
    className: 'device-avg-discharge-rate-chart',
    hasLegend: false,
    bottomBorder: false,
    columnWidth: 60,
    yAxisType: 'value',
    useAxisTooltip: true,
    amchartsYAxisNumberFormat: '#.',
    categoryKey: 'bucket',
    drawXAxis: (chart, xAxis) => {
      xAxis.title.text = t('DEVICE_AVG_DISCHARGE_CHART_LABEL');
      xAxis.renderer.labels.template.fontSize = 12;
      xAxis.renderer.labels.template.horizontalCenter = 'middle';
      xAxis.renderer.labels.template.verticalCenter = 'middle';
      xAxis.renderer.minGridDistance = 20;
      return xAxis;
    },
    drawYAxis: (chart, yAxis) => {
      yAxis.title.text = t('NO_OF_DEVICES');
      yAxis.title.fontSize = 12;
      yAxis.renderer.labels.template.fontSize = 12;
      yAxis.min = 0;
      yAxis.strictMinMax = false;
      yAxis.renderer.minGridDistance = 30;
      yAxis.maxPrecision = 0;
      return yAxis;
    },
    getAxisTooltipAdapter: () => createChartTooltipAdapter(t, true)
  };

  return (
    <Card noPadding={false} className="device-avg-discharge-rate-container">
      <StackedColumnCategoryChart<any>
        {...chartProps}
      />
    </Card>
  );
}
